import React from "react";
import { motion } from "framer-motion";
import { Globe, Sparkles, Instagram, Image, Palette, Settings, Rocket, ArrowUpRight } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

const ICONS = [Globe, Sparkles, Instagram, Image, Palette, Settings, Rocket];

export default function Services({ onOpenLead }) {
  const { t } = useLang();
  // Bento layout spans
  const SPANS = [
    "md:col-span-7", // Websites - wide
    "md:col-span-5", // AI gen
    "md:col-span-4", // Social
    "md:col-span-4", // Posts
    "md:col-span-4", // Branding
    "md:col-span-6", // Digital setup
    "md:col-span-6", // Launch kit
  ];

  return (
    <section id="services" className="py-20 md:py-28 bg-slate-50" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">{t.services.eyebrow}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 text-slate-950">{t.services.title}</h2>
          </div>
          <button
            onClick={onOpenLead}
            data-testid="services-cta-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-900"
          >
            Discuss your project <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          {t.services.list.map((s, i) => {
            const Icon = ICONS[i] || Sparkles;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                className={`group relative ${SPANS[i]} rounded-2xl bg-white border border-slate-200 p-7 md:p-8 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 transition-all duration-300 overflow-hidden`}
                data-testid={`service-card-${i}`}
              >
                {/* Decorative gradient blob */}
                <div className="absolute -top-16 -right-16 w-48 h-48 bg-gradient-to-br from-blue-100 to-violet-100 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition duration-500" />

                <div className="relative flex flex-col h-full min-h-[160px]">
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-slate-950 group-hover:bg-gradient-to-br group-hover:from-blue-600 group-hover:to-violet-600 flex items-center justify-center transition">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    {s.tag && (
                      <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider">
                        {s.tag}
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-bold mt-5 text-slate-950">{s.t}</h3>
                  <p className="text-slate-600 mt-2 text-sm leading-relaxed">{s.d}</p>
                  <button
                    onClick={onOpenLead}
                    className="mt-auto pt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 group-hover:gap-2 transition-all"
                    data-testid={`service-cta-${i}`}
                  >
                    Get started
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
